/*! Copyright (c) Safe As Milk. All rights reserved. */
import Player, { YoutubeProvider, VimeoProvider } from "vlite";

import { isTouchScreen } from "utils";

const VIDEO_TYPES = [ "html5", "youtube", "vimeo" ];

Player.registerProvider("youtube", YoutubeProvider);

Player.registerProvider("vimeo", VimeoProvider);

class VideoPlayer extends HTMLElement {
    #boundHandleOverlayClick;
    #boundPlay;
    #isReady=false;
    #isPlaying=false;
    #loading=false;
    #loadingIndicator;
    #player=null;
    #posterElement;
    #posterUrl;
    #isPlayScheduled=false;
    #touchScreenControlsListenerSetUp=false;
    #type;
    #videoElementContainer;
    constructor() {
        super();
        this.#boundHandleOverlayClick = this.#handleOverlayClick.bind(this);
        this.#boundPlay = this.play.bind(this);
    }
    connectedCallback() {
        this.#type = this.dataset.type;
        if (!this.#type || !VIDEO_TYPES.includes(this.#type)) throw new Error('Video type must be provided and one of: "html5", "Youtube", "Vimeo"');
        this.#videoElementContainer = this.querySelector("video-element");
        this.#loadingIndicator = this.querySelector("video-loading-indicator");
        this.#posterUrl = this.dataset.poster;
        this.#posterElement = this.querySelector("video-poster");
        if (this.#posterElement) {
            this.#posterElement.addEventListener("click", this.loadContent.bind(this), {
                once: true
            });
            if (this.#type === "vimeo") this.#updateVimeoPoster();
        }
    }
    disconnectedCallback() {
        this.#loading = false;
        this.reset();
        if (this.#isPlayScheduled) {
            this.removeEventListener("on:video-player:play", this.#isPlayScheduled);
            this.#isPlayScheduled = null;
        }
        if (this.#loadingIndicator) this.#loadingIndicator.setAttribute("hidden", "");
        if (this.#touchScreenControlsListenerSetUp) {
            const overlay = this.querySelector(".v-overlay");
            if (overlay) {
                overlay.removeEventListener("click", this.#boundHandleOverlayClick);
                this.#touchScreenControlsListenerSetUp = false;
            }
        }
    }
    get ready() {
        return this.#isReady;
    }
    get playing() {
        return this.#isPlaying;
    }
    loadContent() {
        if (this.hasAttribute("loaded") || this.#loading) return this;
        this.#videoElementContainer.appendChild(this.querySelector("template").content.firstElementChild.cloneNode(true));
        const videoElement = this.querySelector("[video-element]");
        if (!videoElement) throw new Error("Video element not found");
        if (this.#loadingIndicator) this.#loadingIndicator.removeAttribute("hidden", "");
        const createPlayer = () => {
            new Player(videoElement, {
                options: {
                    autoHide: true,
                    autoHideDelay: 1e3,
                    controls: this.hasAttribute("data-controls"),
                    loop: this.hasAttribute("data-loop"),
                    muted: this.hasAttribute("data-muted"),
                    playsinline: this.hasAttribute("data-playsinline"),
                    ...this.#posterUrl ? {
                        poster: this.#posterUrl
                    } : {},
                    ...this.hasAttribute("data-exclude-controls") ? this.dataset.excludeControls.split(",").reduce(((controls, control) => ({
                        ...controls,
                        [control]: false
                    })), {}) : {}
                },
                provider: this.#type,
                onReady: player => {
                    this.#player = player;
                    if (this.#type === "html5") {
                        this.#player.methodPlay = () => {
                            this.#player.media.play().catch((e => {
                                if (e.name === "NotAllowedError") {
                                    if (this.#posterElement) this.#posterElement.classList.remove("is-hidden");
                                    this.#player.pause();
                                }
                            }));
                        };
                    } else if (this.#type === "youtube") {
                        this.#player.instance.addEventListener("onAutoplayBlocked", (() => {
                            const overlay = this.querySelector(".v-overlay");
                            if (overlay) overlay.addEventListener("click", (() => {
                                this.#player.play();
                            }), {
                                once: true
                            });
                        }));
                    }
                    this.#isReady = true;
                    this.#loading = false;
                    this.setAttribute("loaded", "");
                    if (this.#loadingIndicator) this.#loadingIndicator.setAttribute("hidden", "");
                    const playButtons = this.querySelectorAll(".v-bigPlay");
                    playButtons.forEach((playButton => {
                        const playIcon = document.getElementById("template-icon-play").content;
                        playButton.innerHTML = "";
                        playButton.appendChild(playIcon.cloneNode(true));
                    }));
                    if (isTouchScreen()) {
                        const overlay = this.querySelector(".v-overlay");
                        if (overlay) {
                            overlay.addEventListener("click", this.#boundHandleOverlayClick);
                            this.#touchScreenControlsListenerSetUp = true;
                        }
                    }
                    this.#player.on("play", (() => {
                        this.#isPlaying = true;
                        this.classList.add("is-playing");
                        this.classList.remove("is-suspended");
                        if (this.#posterElement) this.#posterElement.classList.add("is-hidden");
                        this.dispatchEvent(new CustomEvent("on:video-player:playing", {
                            detail: {
                                player: this.#player
                            }
                        }));
                    }));
                    this.#player.on("pause", (() => {
                        this.#isPlaying = false;
                        this.classList.remove("is-playing");
                        this.dispatchEvent(new CustomEvent("on:video-player:paused", {
                            detail: {
                                player: this.#player
                            }
                        }));
                    }));
                    this.#player.on("ended", (() => {
                        this.#isPlaying = false;
                        this.classList.remove("is-playing");
                        this.dispatchEvent(new CustomEvent("on:video-player:ended", {
                            detail: {
                                player: this.#player
                            }
                        }));
                    }));
                    this.#player.media.addEventListener("suspend", (() => {
                        this.classList.add("is-suspended");
                        this.dispatchEvent(new CustomEvent("on:video-player:suspended", {
                            detail: {
                                player: this.#player
                            }
                        }));
                    }));
                    this.dispatchEvent(new CustomEvent("on:video-player:ready", {
                        detail: {
                            player: this.#player
                        }
                    }));
                }
            });
        };
        if (this.#type === "vimeo" && this.#posterElement.dataset.videoId && this.#posterElement.children.length === 0 && this.#posterUrl === undefined) {
            const interval = setInterval((() => {
                if (typeof this.#posterUrl === "string") {
                    createPlayer();
                    clearInterval(interval);
                }
            }), 50);
        } else {
            createPlayer();
        }
        return this;
    }
    play() {
        if (this.#isPlaying) return this;
        if (this.#player) {
            this.#isPlayScheduled = false;
            this.#player.play();
        } else if (!this.#isPlayScheduled) {
            this.#isPlayScheduled = true;
            this.addEventListener("on:video-player:ready", this.#boundPlay, {
                once: true
            });
        }
        return this;
    }
    pause() {
        if (!this.#isPlaying) return this;
        if (this.#player) {
            this.#player.pause();
        } else if (this.#isPlayScheduled) {
            this.#isPlayScheduled = false;
            this.removeEventListener("on:video-player:ready", this.#boundPlay);
        }
        return this;
    }
    on(type, handler, options) {
        this.addEventListener(type, handler, options);
        return this;
    }
    off(type, handler, options) {
        this.removeEventListener(type, handler, options);
        return this;
    }
    reset() {
        if (!this.#player) return this;
        this.#player.destroy();
        this.#player = null;
        this.#isReady = false;
        this.#videoElementContainer.innerHTML = "";
        this.removeAttribute("loaded");
        return this;
    }
    #handleOverlayClick() {
        if (!this.#player) return;
        if (this.#player.elements.controlBar) {
            this.#player.elements.controlBar.classList.toggle("v-hidden");
        }
    }
    async #updateVimeoPoster() {
        if (!this.#posterElement) return;
        if (this.#posterElement.dataset.videoId && this.#posterElement.children.length === 0) {
            try {
                const data = await fetch(`https://vimeo.com/api/v2/video/${this.#posterElement.dataset.videoId}.json`);
                const [videoData] = await data.json();
                const {thumbnail_large: thumbnail} = videoData;
                this.#posterUrl = thumbnail;
                this.#posterElement.innerHTML = `<img src="${thumbnail}">`;
            } catch (e) {
                this.#posterUrl = "";
                console.error(`Could not load Vimeo poster: ${e}`);
            }
        }
    }
}

customElements.define("video-player", VideoPlayer);

